/**
 * PipelinePrerequisites Component
 *
 * Shows prerequisite status indicators above a pipeline step.
 */

import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface Prerequisite {
  id: string;
  label: string;
  status: 'met' | 'partial' | 'missing';
  current?: number;
  recommended?: number;
  tooltip?: string;
}

interface PipelinePrerequisitesProps {
  prerequisites: Prerequisite[];
}

const statusIcons = {
  met: CheckCircle2,
  partial: AlertCircle,
  missing: XCircle
};

const statusColors = {
  met: 'text-green-500',
  partial: 'text-yellow-500',
  missing: 'text-red-500'
};

export function PipelinePrerequisites({ prerequisites }: PipelinePrerequisitesProps) {
  if (!prerequisites || prerequisites.length === 0) {
    return <div className="h-6" />; // Spacer for alignment
  }

  return (
    <TooltipProvider>
      <div className="flex items-center justify-center gap-1 mb-2 min-h-[24px]">
        {prerequisites.map((prereq) => {
          const Icon = statusIcons[prereq.status];
          const tooltipText = prereq.tooltip ||
            `${prereq.label}: ${prereq.status === 'met' ? 'Complete' : prereq.status === 'partial' ? 'Partial' : 'Missing'}`;

          return (
            <Tooltip key={prereq.id}>
              <TooltipTrigger asChild>
                <div className={cn(
                  'flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs',
                  prereq.status === 'met' && 'bg-green-100 dark:bg-green-900/30',
                  prereq.status === 'partial' && 'bg-yellow-100 dark:bg-yellow-900/30',
                  prereq.status === 'missing' && 'bg-red-100 dark:bg-red-900/30'
                )}>
                  <Icon className={cn('h-3 w-3', statusColors[prereq.status])} />
                  <span className={cn(
                    'text-xs truncate max-w-[60px]',
                    statusColors[prereq.status]
                  )}>
                    {prereq.label.split(' ')[0]}
                  </span>
                  {prereq.current !== undefined && prereq.recommended && (
                    <span className={cn('text-xs', statusColors[prereq.status])}>
                      {prereq.current}/{prereq.recommended}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
